import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { Logo } from '@/components/Logo';
import { buttonVariants } from '@/components/ui/Button';
import {
  ChevronDown,
  Gamepad2,
  Home,
  ListVideo,
  LogOut,
  Menu,
  Search,
  Settings,
  Shield,
  ShoppingBag,
  ShoppingCart,
  X,
} from '@/components/icons';
import { Bell, Users } from 'lucide-react';
import { ProfileAvatar } from '@/components/ProfileAvatar';
import { authApi } from '@/features/auth/api';
import { useAuth, useRefreshViewer } from '@/features/auth/AuthContext';

const NAV = [
  { label: 'Home', to: '/browse', icon: Home },
  { label: 'Games', to: '/games', icon: Gamepad2 },
  { label: 'Store', to: '/store', icon: ShoppingBag },
] as const;

function navLinkClass({ isActive }: { isActive: boolean }) {
  return cn(
    'inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium transition-colors',
    isActive ? 'text-ink bg-surface-raised' : 'text-ink-muted hover:text-ink hover:bg-surface-raised/60',
  );
}

function AccountMenu() {
  const { user, viewer, profiles, activeProfile } = useAuth();
  const refreshViewer = useRefreshViewer();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  if (!user) return null;

  const unread = viewer?.unreadNotifications ?? 0;

  const isStaff =
    user.role === 'owner' || user.role === 'admin' || user.role === 'contributor';
  const initial = (user.displayName || user.email).charAt(0).toUpperCase();

  async function signOut() {
    setOpen(false);
    await authApi.logout().catch(() => undefined);
    await refreshViewer();
    navigate('/');
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="relative flex items-center gap-1.5 rounded-full border border-border bg-surface py-1 pl-1 pr-2 transition-colors hover:bg-surface-raised"
      >
        {activeProfile ? (
          <ProfileAvatar profile={activeProfile} size={28} className="!rounded-full" />
        ) : (
          <span className="grid h-7 w-7 place-items-center rounded-full bg-brand-gradient text-xs font-bold text-white">
            {initial}
          </span>
        )}
        <ChevronDown size={15} className="text-ink-muted" />
        {unread > 0 && (
          <span className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-danger px-1 text-[10px] font-bold leading-none text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div
            role="menu"
            className="absolute right-0 z-50 mt-2 w-60 overflow-hidden rounded-card border border-border bg-surface-raised shadow-card"
          >
            <div className="border-b border-border px-4 py-3">
              <div className="flex items-center gap-2.5">
                {activeProfile && <ProfileAvatar profile={activeProfile} size={34} />}
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-ink">{activeProfile?.name ?? user.displayName}</p>
                  <p className="truncate text-xs text-ink-faint">{user.email}</p>
                </div>
              </div>
              {isStaff && (
                <span className="mt-2 inline-block rounded-full bg-brand/15 px-2 py-0.5 text-2xs font-semibold uppercase tracking-wide text-brand-soft">
                  Staff
                </span>
              )}
            </div>
            <div className="py-1.5 text-sm">
              {profiles.length > 1 && (
                <MenuLink to="/profiles" icon={Users} onClick={() => setOpen(false)}>
                  Switch profile
                </MenuLink>
              )}
              <MenuLink to="/account" icon={Settings} onClick={() => setOpen(false)}>
                Account
              </MenuLink>
              <MenuLink to="/my-list" icon={ListVideo} onClick={() => setOpen(false)}>
                My List
              </MenuLink>
              <Link
                to="/notifications"
                role="menuitem"
                onClick={() => setOpen(false)}
                className="flex items-center justify-between gap-2.5 px-4 py-2 text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink"
              >
                <span className="flex items-center gap-2.5"><Bell size={16} /> Notifications</span>
                {unread > 0 && (
                  <span className="grid h-5 min-w-5 place-items-center rounded-full bg-danger px-1.5 text-2xs font-bold text-white">
                    {unread > 9 ? '9+' : unread}
                  </span>
                )}
              </Link>
              {isStaff && (
                <MenuLink to="/admin" icon={Shield} onClick={() => setOpen(false)}>
                  Dev Panel
                </MenuLink>
              )}
              <button
                type="button"
                role="menuitem"
                onClick={signOut}
                className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink"
              >
                <LogOut size={16} />
                Sign out
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function MenuLink({
  to,
  icon: Icon,
  onClick,
  children,
}: {
  to: string;
  icon: typeof Home;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      role="menuitem"
      onClick={onClick}
      className="flex items-center gap-2.5 px-4 py-2 text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink"
    >
      <Icon size={16} />
      {children}
    </Link>
  );
}

export function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-bg/80 shadow-header backdrop-blur-xl">
      <div className="container-page flex h-16 items-center justify-between gap-4">
        <div className="flex items-center gap-6">
          <Link to={user ? '/browse' : '/'} aria-label="BingeBox home" className="rounded-md">
            <Logo />
          </Link>
          <nav className="hidden items-center gap-1 md:flex" aria-label="Primary">
            {NAV.map(({ label, to, icon: Icon }) => (
              <NavLink key={to} to={to} className={navLinkClass}>
                <Icon size={18} strokeWidth={1.75} />
                {label}
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-1.5">
          <Link
            to="/search"
            aria-label="Search"
            className="grid h-10 w-10 place-items-center rounded-full text-ink-muted transition-colors hover:bg-surface-raised hover:text-ink"
          >
            <Search size={19} strokeWidth={1.75} />
          </Link>
          <Link
            to="/store"
            aria-label="Cart"
            className="grid h-10 w-10 place-items-center rounded-full text-ink-muted transition-colors hover:bg-surface-raised hover:text-ink"
          >
            <ShoppingCart size={19} strokeWidth={1.75} />
          </Link>

          {user ? (
            <div className="ml-1 hidden sm:block">
              <AccountMenu />
            </div>
          ) : (
            <div className="ml-1 hidden items-center gap-2 sm:flex">
              <Link to="/login" className={buttonVariants({ variant: 'ghost', size: 'sm' })}>
                Sign in
              </Link>
              <Link to="/plans" className={buttonVariants({ variant: 'primary', size: 'sm' })}>
                Get started
              </Link>
            </div>
          )}

          <button
            type="button"
            className="grid h-10 w-10 place-items-center rounded-full text-ink-muted transition-colors hover:bg-surface-raised hover:text-ink md:hidden"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-border bg-panel md:hidden">
          <nav className="container-page flex flex-col gap-1 py-3" aria-label="Mobile">
            {NAV.map(({ label, to, icon: Icon }) => (
              <NavLink key={to} to={to} className={navLinkClass}>
                <Icon size={18} strokeWidth={1.75} />
                {label}
              </NavLink>
            ))}
            <div className="mt-2 flex flex-col gap-2 border-t border-border pt-3">
              {user ? (
                <>
                  <NavLink to="/account" className={navLinkClass}>
                    <Settings size={18} strokeWidth={1.75} />
                    Account
                  </NavLink>
                  <NavLink to="/my-list" className={navLinkClass}>
                    <ListVideo size={18} strokeWidth={1.75} />
                    My List
                  </NavLink>
                  {(user.role === 'owner' ||
                    user.role === 'admin' ||
                    user.role === 'contributor') && (
                    <NavLink to="/admin" className={navLinkClass}>
                      <Shield size={18} strokeWidth={1.75} />
                      Dev Panel
                    </NavLink>
                  )}
                </>
              ) : (
                <div className="flex gap-2">
                  <Link
                    to="/login"
                    className={buttonVariants({ variant: 'secondary', size: 'md', className: 'flex-1' })}
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/plans"
                    className={buttonVariants({ variant: 'primary', size: 'md', className: 'flex-1' })}
                  >
                    Get started
                  </Link>
                </div>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
